import _ from 'lodash';
import type { Store, Reducer, Middleware } from 'redux';
import { createStore, applyMiddleware, compose } from 'redux';
import thunk from 'redux-thunk';
import { composeWithDevTools } from '@redux-devtools/extension';
import config from '../../config';
import { HYDRATE_STATE } from './consts';

const isProd = config.env === 'prod';

const actionSanitizer = (action: $TSFixMe) => action;

type StateSanitizer = (state: $TSFixMe, index: number) => $TSFixMe;

// https://github.com/zalmoxisus/redux-devtools-extension/blob/master/docs/Troubleshooting.md#excessive-use-of-memory-and-cpu
const getComposeEnhancers = (stateSanitizer: StateSanitizer) =>
    isProd
        ? compose
        : composeWithDevTools({
              actionSanitizer,
              stateSanitizer
          }) || compose;

const BUILD_ID = process.env.BUILD_ID;

export const configureStore = ({
    initialState,
    rootReducer,
    middlewares,
    localStorePaths,
    onLoadError,
    stateSanitizer = (state) => state
}: {
    initialState?: $TSFixMe;
    rootReducer: Reducer;
    middlewares?: Middleware[];
    localStorePaths?: { [K: string]: string[] };
    stateSanitizer?: StateSanitizer;
    onLoadError?: (error: Error) => void;
}): Store => {
    let cachedStore = localStorePaths && getLocalStorageValue({ onLoadError });
    if (cachedStore?._buildId !== BUILD_ID) {
        removeLocalStorageValue();
        cachedStore = undefined;
    }

    const store = createStore(rootReducer, { ...initialState }, getComposeEnhancers(stateSanitizer)(applyMiddleware(thunk, ...(middlewares ?? []))));

    if (localStorePaths && !_.isEmpty(localStorePaths)) {
        // hydrate the Redux store from localStorage
        hydrateState(localStorePaths, store, cachedStore);

        // save to localStorage when Redux store changes
        store.subscribe(
            _.throttle(() => {
                const values = Object.keys(localStorePaths).reduce(
                    (acc, key) => {
                        const paths = localStorePaths[key];
                        const state = store.getState()[key];
                        if (state && paths) {
                            acc[key] = _.pick(state, paths);
                        }
                        return acc;
                    },
                    {} as { [K: string]: $TSFixMe }
                );
                values._buildId = BUILD_ID;
                setLocalStorageValue(values);
            }, 1000)
        );
    }

    return store;
};

const hydrateState = (localStorePaths: { [K: string]: string[] }, store: Store, localStorage?: $TSFixMe): $TSFixMe => {
    if (!localStorage) {
        return;
    }

    const state = store.getState();
    const hydratedState = JSON.parse(JSON.stringify(state));

    const keys = Object.keys(localStorePaths);
    keys.forEach((key) => {
        localStorePaths[key]!.forEach((path: string) => {
            const prefixedPath = `${key}.${path}`;
            const value = _.get(localStorage, prefixedPath);
            _.set(hydratedState, prefixedPath, value);
        });
    });

    store.dispatch({
        type: HYDRATE_STATE,
        data: {
            ...hydratedState
        }
    });
};

const LOCAL_STORAGE_KEY = 'stackbit';

const getLocalStorageValue = ({ onLoadError }: { onLoadError?: (error: Error) => void }): $TSFixMe | null => {
    try {
        const valueStr = window.localStorage.getItem(LOCAL_STORAGE_KEY);
        return JSON.parse(valueStr!);
    } catch (error) {
        onLoadError?.(error as Error);
    }

    return null;
};

const setLocalStorageValue = (value: $TSFixMe): void => {
    try {
        window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(value));
        // eslint-disable-next-line no-empty
    } catch (e) {}
};

const removeLocalStorageValue = (): void => {
    try {
        window.localStorage.removeItem(LOCAL_STORAGE_KEY);
        // eslint-disable-next-line no-empty
    } catch (e) {}
};
